import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAction, useApi } from '../lib/useApi';
import { ErrorBox, Field, Loading, Notice, PageHeader, StatusBadge, fmtDate, LoadFailed } from '../components/ui';
import { AVATAR } from './DashboardPage';

interface Consent {
  id: string;
  type: string;
  method: string;
  status: string;
  child_first_name: string | null;
  child_profile_id: string | null;
  verified_at: string | null;
  revoked_at: string | null;
}

const METHODS = [
  { key: 'card_check', label: 'Card check (fastest)', hint: 'A secure Stripe check of a payment card in your name. Nothing is charged.' },
  { key: 'signed_form', label: 'Signed consent form', hint: 'Upload or post a signed form; our safeguarding team verifies it.' },
  { key: 'id_check', label: 'Photo ID check', hint: 'A trained reviewer confirms you are an adult. We never store the ID image.' },
  { key: 'video_call', label: 'Short video call', hint: 'A 5-minute call with our team.' },
];

const PURPOSES = [
  { key: 'voice_recording', title: 'Voice practice recordings', text: 'Your child can record themselves saying words. Clips are deleted automatically after the number of days you choose.' },
  { key: 'live_circles', title: 'Live Circles', text: 'Your child can join tutor-led group sessions (at least 2 children, you can always join).' },
  { key: 'session_recording', title: 'Recording of Live Circles', text: 'Circles are recorded so safeguarding staff can review any concern. Required for Live Circles.' },
  { key: 'ai_processing', title: 'AI help with family stories', text: 'Typed or rough transcripts of elder recordings are sent to our AI provider to tidy and translate. A family member always reviews the result.' },
  {
    key: 'ai_image_processing',
    title: 'Reading old letters and documents',
    text: 'Separate from the setting above, and off unless you turn it on. Photographs of letters, certificates and the backs of pictures are sent to our AI provider so the writing can be read and kept with them. A photograph carries more than you typed — an address, a signature, a face — which is why this is its own choice. Anything you mark “restricted” is never sent, whatever this says.',
  },
  { key: 'ai_training', title: 'Help improve Yoruba AI (optional)', text: 'Off by default. Allows selected family story recordings (never children’s practice clips) to be used to improve language models.' },
];

export function ConsentPage() {
  const { data, error, loading, reload } = useApi<{ consents: Consent[]; methodsAvailable?: Record<string, boolean> }>('/consents');
  const act = useAction();
  const nav = useNavigate();
  const [childName, setChildName] = useState('');
  const [method, setMethod] = useState('');
  const [ack, setAck] = useState(false);
  const [evidence, setEvidence] = useState<Record<string, string>>({});
  const [childForm, setChildForm] = useState<Record<string, { age: string; avatar: string }>>({});

  if (loading) return <Loading />;
  if (error || !data) return <LoadFailed error={error} onRetry={reload} />;
  const consents = data.consents;
  // Only offer routes that can actually finish today. Card check needs Stripe connected.
  const methods = METHODS.filter((m) => data.methodsAvailable?.[m.key] !== false);
  const chosen = method || methods[0]?.key || '';
  const childConsents = consents.filter((c) => c.type === 'child_account');
  const hasVpc = childConsents.some((c) => c.status === 'verified');
  const active = (type: string) => consents.find((c) => c.type === type && c.status === 'verified');

  return (
    <>
      <PageHeader
        title="Parental consent"
        lead="Before we hold any information about a child, a parent or guardian must give verifiable consent (as required by COPPA and the UK Age Appropriate Design Code). You can withdraw consent at any time."
      />
      <Notice text={act.notice} />
      <ErrorBox error={act.error} />

      <section className="card stack">
        <h2>1 · Consent for a child’s profile</h2>
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await act.run(() => api<{ consent: Consent }>('/consents', { body: { type: 'child_account', method: chosen, childFirstName: childName, acknowledged: ack } }));
            if (!r) return;
            if (chosen === 'card_check') {
              const v = await act.run(() => api<{ verified: boolean; checkoutUrl?: string }>(`/consents/${r.consent.id}/card-check`, { method: 'POST', body: {} }), 'Consent verified.');
              if (v?.checkoutUrl) window.location.href = v.checkoutUrl;
            }
            setChildName('');
            setAck(false);
            reload();
          }}
        >
          <Field id="cn" label="Child’s first name or nickname" hint="Just a first name. We never ask for surnames, photos, school or date of birth.">
            <input id="cn" className="yo" value={childName} onChange={(e) => setChildName(e.target.value)} required maxLength={40} />
          </Field>
          <fieldset className="field" style={{ border: 0, padding: 0 }}>
            <legend className="label">How would you like to verify?</legend>
            {methods.map((m) => (
              <label key={m.key} className="check">
                <input type="radio" name="method" value={m.key} checked={chosen === m.key} onChange={() => setMethod(m.key)} />
                <span>
                  <strong>{m.label}</strong> <span className="muted small">— {m.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <label className="check">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} required />
            <span>
              I am this child’s parent or legal guardian. I consent to Ìran creating a private profile (first name and age band only) so they can learn our
              family language. I understand I can review, export or delete my child’s data at any time.
            </span>
          </label>
          <button className="btn" disabled={act.busy || !ack}>
            Give consent
          </button>
        </form>
      </section>

      <section className="card stack">
        <h2>Your consent records</h2>
        {childConsents.length === 0 && <p className="muted">None yet.</p>}
        {childConsents.map((c) => (
          // The create-a-child form gets a line of its own below the record.
          // Sharing a wrapping row with the Withdraw button scattered the age
          // box, the avatar picker and the button across three lines, so the
          // one form a parent must fill in looked like debris.
          <div key={c.id} className="stack tight" style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
            <div className="spread">
              <div>
                <strong className="yo">{c.child_first_name}</strong> · {c.method.replace('_', ' ')} · <StatusBadge status={c.status} />
                {c.verified_at && <span className="muted small"> verified {fmtDate(c.verified_at)}</span>}
              </div>
              <div className="row">
              {c.status === 'pending' && c.method === 'card_check' && (
                <button
                  className="btn small"
                  onClick={async () => {
                    const v = await act.run(() => api<{ checkoutUrl?: string }>(`/consents/${c.id}/card-check`, { method: 'POST', body: {} }), 'Consent verified.');
                    if (v?.checkoutUrl) window.location.href = v.checkoutUrl;
                    reload();
                  }}
                >
                  Verify with card
                </button>
              )}
              {c.status === 'pending' && c.method !== 'card_check' && (
                <form
                  className="row"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await act.run(() => api(`/consents/${c.id}/evidence`, { body: { reference: evidence[c.id] ?? '' } }), 'Sent for verification.');
                  }}
                >
                  <input aria-label="Reference" placeholder="Form/booking reference" value={evidence[c.id] ?? ''} onChange={(e) => setEvidence({ ...evidence, [c.id]: e.target.value })} required />
                  <button className="btn small secondary">Submit</button>
                </form>
              )}
              {c.status !== 'revoked' && (
                <button className="btn small ghost" onClick={async () => { if (confirm('Withdraw this consent? The child profile will be switched off.')) { await act.run(() => api(`/consents/${c.id}/revoke`, { method: 'POST', body: {} }), 'Consent withdrawn.'); reload(); } }}>
                  Withdraw
                </button>
              )}
              </div>
            </div>

            {c.status === 'verified' && !c.child_profile_id && (
              <div className="child-form">
                <p className="small muted" style={{ margin: 0 }}>
                  <strong className="yo">{c.child_first_name}</strong> is approved. Give an age and pick a face, and their profile is made — no surname, no photograph, no
                  email address.
                </p>
                <form
                  className="row"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const f = childForm[c.id] ?? { age: '', avatar: 'cowrie' };
                    const r = await act.run(() => api('/family/children', { body: { consentId: c.id, displayName: c.child_first_name, age: Number(f.age), avatarKey: f.avatar } }), 'Child profile created.');
                    if (r) nav('/');
                  }}
                >
                  <label className="small" htmlFor={`age-${c.id}`}>Age</label>
                  <input id={`age-${c.id}`} type="number" min={4} max={17} required style={{ width: 80 }} value={childForm[c.id]?.age ?? ''} onChange={(e) => setChildForm({ ...childForm, [c.id]: { avatar: childForm[c.id]?.avatar ?? 'cowrie', age: e.target.value } })} />
                  <select aria-label="Avatar" value={childForm[c.id]?.avatar ?? 'cowrie'} onChange={(e) => setChildForm({ ...childForm, [c.id]: { age: childForm[c.id]?.age ?? '', avatar: e.target.value } })}>
                    {Object.entries(AVATAR).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v} {k}
                      </option>
                    ))}
                  </select>
                  <button className="btn small">Create profile</button>
                </form>
              </div>
            )}
          </div>
        ))}
      </section>

      <section className="card stack">
        <h2>2 · What your children can do</h2>
        {!hasVpc && <p className="muted">These unlock after your first verified child consent.</p>}
        {PURPOSES.map((p) => {
          const c = active(p.key);
          return (
            <div key={p.key} className="spread" style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <div style={{ maxWidth: '70ch' }}>
                <strong>{p.title}</strong> {c ? <StatusBadge status="verified" /> : <span className="badge">off</span>}
                <p className="small muted" style={{ margin: 0 }}>{p.text}</p>
              </div>
              {c ? (
                <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/consents/${c.id}/revoke`, { method: 'POST', body: {} }), 'Turned off.'); reload(); }}>
                  Turn off
                </button>
              ) : (
                <button className="btn small secondary" disabled={!hasVpc || act.busy} onClick={async () => { await act.run(() => api('/consents', { body: { type: p.key, method: 'in_app_confirmation', acknowledged: true } }), 'Consent recorded.'); reload(); }}>
                  I agree — turn on
                </button>
              )}
            </div>
          );
        })}
      </section>
    </>
  );
}
